import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import {
  appThemeRadius,
  appThemeShadow,
  appThemeSize,
} from "styles/appThemeTokens";
import { dataTableToolbarContainerSx, DATA_TABLE_TOOLBAR_CONTAINER } from "./tableToolbarButton.styles";

const uiFont = appThemeFonts.family.ui;
const numericFont = appThemeFonts.family.numeric;
const primary = appThemeColors.text.lightBlue;

const focusRing = {
  outline: "2px solid #0B5FA5",
  outlineOffset: "2px",
};

const filterFieldBorder = {
  borderColor: appThemeColors.border.strong,
  borderWidth: 1,
};

const filterFieldFocusedBorder = {
  borderColor: appThemeColors.text.lightBlue,
  borderWidth: "2px !important",
};

export const CheckboxUncheckedIcon = styled("span")({
  width: 16,
  height: 16,
  boxSizing: "border-box",
  borderRadius: 3,
  border: `1px solid ${appThemeColors.border.strong}`,
  backgroundColor: appThemeColors.background.paper,
  display: "block",
  flexShrink: 0,
});

export const CheckboxCheckedIcon = styled("span")({
  width: 16,
  height: 16,
  boxSizing: "border-box",
  borderRadius: 3,
  border: 0,
  backgroundColor: primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const CheckboxIndeterminateIcon = styled(CheckboxCheckedIcon)({
  "&::after": {
    content: '""',
    width: 8,
    height: 2,
    borderRadius: 1,
    backgroundColor: appThemeColors.neutral.white,
  },
});

export const CheckboxCheckMark = styled(CheckIcon)({
  fontSize: 12,
  color: appThemeColors.neutral.white,
});

export const TableCheckbox = styled(Checkbox)({
  padding: 0,
  margin: 0,
  color: appThemeColors.border.strong,
  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
    color: primary,
  },
  "&:hover": {
    backgroundColor: "transparent",
  },
  "& .MuiSvgIcon-root": {
    fontSize: 16,
  },
  "&:focus-visible": {
    ...focusRing,
    borderRadius: 4,
  },
});

export const CheckboxTableCell = styled(TableCell)({
  width: "44px !important",
  minWidth: "44px !important",
  maxWidth: "44px !important",
  padding: "0 0 0 14px !important",
  overflow: "visible !important",
  verticalAlign: "middle",
  boxSizing: "border-box",
});

export const CellTextField = styled(TextField)(() => ({
  fontFamily: uiFont,
  "& .MuiInputBase-root": {
    fontFamily: uiFont,
    fontSize: "12.5px",
  },
}));

export const CellSelect = styled(Select)(() => ({
  width: "100%",
  fontFamily: uiFont,
  fontSize: "11.5px",
  height: appThemeSize.tableFilterControlHeight,
  borderRadius: 6,
  backgroundColor: appThemeColors.background.paper,
  "& .MuiOutlinedInput-notchedOutline": {
    ...filterFieldBorder,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    ...filterFieldBorder,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    ...filterFieldFocusedBorder,
  },
  "& .MuiSelect-select": {
    padding: "0 22px 0 7px !important",
    display: "flex",
    alignItems: "center",
    height: appThemeSize.tableFilterControlHeight,
    boxSizing: "border-box",
    fontFamily: uiFont,
    fontSize: "11.5px",
    color: appThemeColors.text.primary,
  },
  "& .MuiSelect-icon": {
    right: 2,
    color: appThemeColors.text.muted,
    fontSize: 16,
  },
}));

export const filterSelectMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  marginThreshold: 0,
  PaperProps: {
    sx: {
      marginTop: "5px",
      borderRadius: appThemeRadius.lg,
      border: `1px solid ${appThemeColors.border.default}`,
      boxShadow: appThemeShadow.menu,
      padding: "7px",
      backgroundColor: appThemeColors.background.paper,
      maxHeight: 320,
      fontFamily: uiFont,
    },
  },
  MenuListProps: {
    sx: {
      py: 0,
      fontFamily: uiFont,
    },
  },
};

export const paginationSelectMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  marginThreshold: 0,
  PaperProps: {
    sx: {
      marginTop: "4px",
      borderRadius: appThemeRadius.lg,
      border: `1px solid ${appThemeColors.border.default}`,
      boxShadow: appThemeShadow.menu,
      padding: "4px",
      backgroundColor: appThemeColors.background.paper,
      fontFamily: uiFont,
      "& .MuiMenuItem-root": {
        fontFamily: uiFont,
        fontSize: "12px",
        minHeight: 30,
        lineHeight: 1.3,
        py: "4px",
        px: "8px",
        borderRadius: "5px",
        color: appThemeColors.text.primary,
        "&:hover": {
          backgroundColor: appThemeColors.background.kbd,
        },
        "&.Mui-selected": {
          backgroundColor: `${appThemeColors.background.kbd} !important`,
          fontWeight: 500,
        },
      },
    },
  },
  MenuListProps: {
    sx: {
      py: 0,
      fontFamily: uiFont,
    },
  },
};

export const filterSelectMenuItemSx = {
  borderRadius: appThemeRadius.md,
  fontFamily: uiFont,
  fontSize: "13px",
  color: appThemeColors.text.primary,
  py: "7px",
  px: "10px",
  gap: "8px",
  minHeight: 0,
  fontStyle: "normal",
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
  },
  "&.Mui-selected": {
    backgroundColor: `${appThemeColors.background.kbd} !important`,
    color: appThemeColors.text.primary,
    fontWeight: 500,
    "&:hover": {
      backgroundColor: `${appThemeColors.background.kbd} !important`,
    },
  },
  "&.Mui-focusVisible": {
    backgroundColor: appThemeColors.background.kbd,
  },
};

export const filterSelectMenuItemSelectedStrongSx = {
  ...filterSelectMenuItemSx,
  "&.Mui-selected": {
    backgroundColor: `${primary} !important`,
    color: appThemeColors.neutral.white,
    fontWeight: 500,
    "&:hover": {
      backgroundColor: `${primary} !important`,
    },
  },
};

export const FilterSelectCheckIcon = styled(CheckIcon, {
  shouldForwardProp: (prop) => prop !== "$visible",
})(({ $visible }) => ({
  fontSize: 16,
  color: "inherit",
  visibility: $visible ? "visible" : "hidden",
}));

export const SelectChevronIcon = styled(KeyboardArrowDownIcon)({
  width: "1em",
  height: "1em",
  fontSize: 16,
  top: "calc(50% - 0.5em)",
  right: 4,
  position: "absolute",
  pointerEvents: "none",
  color: appThemeColors.text.muted,
  flexShrink: 0,
});

export const dataTableSectionSx = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
};

export const DataTableLayout = styled(Box)(() => ({
  ...dataTableSectionSx,
}));

export const StyledPaper = styled(Paper)(() => ({
  ...dataTableSectionSx,
  overflow: "hidden",
  backgroundColor: appThemeColors.background.paper,
  border: `1px solid ${appThemeColors.border.default}`,
  borderRadius: appThemeRadius.lg,
  boxShadow: "0 1px 2px rgba(16,42,58,.04)",
  boxSizing: "border-box",
  fontFamily: uiFont,
}));

export const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  ...dataTableToolbarContainerSx,
  flexShrink: 0,
  paddingLeft: "12px !important",
  paddingRight: theme.spacing(2),
  gap: theme.spacing(1.5),
  flexWrap: "wrap",
  justifyContent: "space-between",
  backgroundColor: appThemeColors.background.paper,
  zIndex: 2,
  borderBottom: `1px solid ${appThemeColors.background.soft}`,
  minHeight: "52px !important",
}));

export const ToolbarTitleGroup = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  minWidth: 0,
});

export const ToolbarTitle = styled(Typography)({
  fontFamily: uiFont,
  fontSize: "14px",
  fontWeight: 600,
  color: appThemeColors.text.heading,
  margin: 0,
  lineHeight: 1.3,
});

export const ToolbarTitleAccessory = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  minWidth: 0,
});

export const ToolbarActions = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 8,
  alignItems: "center",
  marginLeft: "auto",
  flexWrap: "nowrap",
  minWidth: 0,
  [`@container ${DATA_TABLE_TOOLBAR_CONTAINER} (max-width: 1040px)`]: {
    gap: 6,
  },
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    width: "100%",
    flexWrap: "wrap",
  },
}));

export const StyledTable = styled(Table, {
  shouldForwardProp: (p) => p !== "$minWidth",
})(({ $minWidth }) => ({
  borderCollapse: "collapse",
  backgroundColor: appThemeColors.background.paper,
  width: "100%",
  maxWidth: "none",
  minWidth: $minWidth ?? undefined,
  tableLayout: "fixed",
  fontFamily: uiFont,
}));

export const StyledTableContainer = styled(TableContainer)(() => {
  const headBg = appThemeColors.background.paper2;
  const filterBg = appThemeColors.background.paper;
  const scrollbarThumb = appThemeColors.text.faintLight;
  const scrollbarBorder = appThemeColors.background.main;

  return {
    flex: 1,
    minHeight: 0,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "auto",
    backgroundColor: appThemeColors.background.paper,
    fontFamily: uiFont,
    scrollbarWidth: "thin",
    scrollbarColor: `${scrollbarThumb} ${scrollbarBorder}`,
    "&::-webkit-scrollbar": {
      width: 10,
      height: 10,
    },
    "&::-webkit-scrollbar-thumb": {
      background: scrollbarThumb,
      borderRadius: 9999,
      border: `2px solid ${scrollbarBorder}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: scrollbarThumb,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "& table": {
      border: "none !important",
      borderRadius: "0 !important",
      width: "100%",
      maxWidth: "none",
      tableLayout: "fixed",
      fontFamily: uiFont,
    },
    "& thead, & tbody, & tfoot, & tr, & th, & td": {
      border: "none !important",
      borderBottom: "none !important",
      fontFamily: "inherit",
    },
    "& .MuiTableHead-root": {
      position: "sticky",
      top: 0,
      zIndex: 5,
      isolation: "isolate",
    },
    "& .MuiTableHead-root .MuiTableRow-root:first-of-type": {
      backgroundColor: headBg,
    },
    "& .MuiTableHead-root .MuiTableRow-root:first-of-type .MuiTableCell-root": {
      position: "relative",
      zIndex: 1,
      backgroundColor: headBg,
      overflow: "hidden",
      borderBottom: `1px solid ${appThemeColors.background.soft} !important`,
    },
    "& .MuiTableHead-root .MuiTableRow-root:first-of-type .MuiTableCell-paddingCheckbox": {
      overflow: "visible !important",
      width: "44px !important",
      minWidth: "44px !important",
      maxWidth: "44px !important",
      padding: "0 0 0 14px !important",
    },
    "& .MuiTableHead-root .MuiTableRow-root:last-of-type": {
      backgroundColor: filterBg,
    },
    "& .MuiTableHead-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
      position: "relative",
      zIndex: 1,
      backgroundColor: `${filterBg} !important`,
      borderBottom: `1px solid ${appThemeColors.border.default} !important`,
    },
    "& .MuiTableHead-root .MuiTableRow-root:last-of-type .MuiTableCell-paddingCheckbox": {
      width: "44px !important",
      minWidth: "44px !important",
      maxWidth: "44px !important",
      padding: "0 0 0 14px !important",
    },
    "& .MuiTableBody-root .MuiTableCell-paddingCheckbox": {
      overflow: "visible !important",
      width: "44px !important",
      minWidth: "44px !important",
      maxWidth: "44px !important",
      padding: "0 0 0 14px !important",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      minHeight: appThemeSize.tableRowHeight,
      transition: "background-color 180ms ease",
      backgroundColor: appThemeColors.background.paper,
      "&:hover .MuiTableCell-root": {
        backgroundColor: `${appThemeColors.background.kbd} !important`,
      },
      "&.Mui-selected .MuiTableCell-root": {
        backgroundColor: `${appThemeColors.background.selected} !important`,
      },
      "&.Mui-selected:hover .MuiTableCell-root": {
        backgroundColor: `${appThemeColors.background.selected} !important`,
      },
    },
    "& .MuiTableBody-root .MuiTableCell-root:not(.MuiTableCell-paddingCheckbox)": {
      fontFamily: uiFont,
      fontSize: "13px",
      color: appThemeColors.text.primary,
      borderBottom: `1px solid ${appThemeColors.background.soft} !important`,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: "12px",
      paddingRight: "12px",
      height: appThemeSize.tableRowHeight,
      whiteSpace: "nowrap",
      verticalAlign: "middle",
      backgroundColor: appThemeColors.background.paper,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& .MuiTableBody-root .MuiTableRow-root:has([data-table-empty-state])": {
      "&:hover .MuiTableCell-root": {
        backgroundColor: `${appThemeColors.background.paper} !important`,
      },
      "& .MuiTableCell-root": {
        borderBottom: "none !important",
        height: "auto !important",
        whiteSpace: "normal !important",
        overflow: "visible !important",
        textOverflow: "clip !important",
      },
    },
  };
});

export const StyledHeaderCell = styled(TableCell, {
  shouldForwardProp: (p) => p !== "$minWidth" && p !== "$width",
})(({ $minWidth, $width }) => ({
  ...($width != null
    ? { width: $width, minWidth: $width }
    : $minWidth != null
      ? { width: $minWidth, minWidth: $minWidth }
      : { minWidth: 0 }),
  fontFamily: uiFont,
  fontWeight: 600,
  fontSize: "11px",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: appThemeColors.text.muted,
  backgroundColor: appThemeColors.background.paper2,
  whiteSpace: "nowrap",
  lineHeight: 1.2,
  verticalAlign: "middle",
  height: appThemeSize.tableHeaderMinHeight,
  padding: "10px 12px 8px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "& .MuiTableSortLabel-root": {
    whiteSpace: "nowrap",
    lineHeight: 1.2,
    alignItems: "center",
    color: "inherit",
    fontFamily: "inherit",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    maxWidth: "100%",
    overflow: "hidden",
    "& .MuiTableSortLabel-icon": {
      color: `${primary} !important`,
      fontSize: "11px",
      opacity: "1 !important",
      flexShrink: 0,
      marginLeft: 4,
      transform: "none !important",
    },
    "&.Mui-active": {
      color: appThemeColors.text.heading,
      "& .MuiTableSortLabel-icon": {
        color: `${primary} !important`,
      },
    },
  },
}));

export const SortBothIcon = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 11,
  height: 11,
  marginLeft: 4,
  fontSize: "11px",
  lineHeight: 1,
  color: primary,
  fontFamily: uiFont,
  flexShrink: 0,
  userSelect: "none",
  transform: "none !important",
});

export const StyledFilterCell = styled(TableCell)(() => ({
  verticalAlign: "middle",
  padding: "9px 12px",
  lineHeight: 1.2,
  minWidth: 0,
  backgroundColor: `${appThemeColors.background.paper} !important`,
  fontFamily: uiFont,
  "& .MuiOutlinedInput-root": {
    height: appThemeSize.tableFilterControlHeight,
    minHeight: appThemeSize.tableFilterControlHeight,
    fontSize: "11.5px",
    fontFamily: uiFont,
    backgroundColor: appThemeColors.background.paper,
    borderRadius: 6,
    minWidth: 0,
    "& fieldset": {
      ...filterFieldBorder,
    },
    "&:hover fieldset": {
      ...filterFieldBorder,
    },
    "&.Mui-focused fieldset": {
      ...filterFieldFocusedBorder,
    },
  },
  "& .MuiInputBase-input": {
    padding: "0 7px",
    fontSize: "11.5px",
    fontFamily: uiFont,
    color: appThemeColors.text.primary,
    height: appThemeSize.tableFilterControlHeight,
    boxSizing: "border-box",
    lineHeight: 1.2,
  },
  "& .MuiInputBase-input::placeholder": {
    color: appThemeColors.text.faint,
    opacity: 1,
  },
  "& .MuiSelect-select": {
    padding: "0 22px 0 7px !important",
    display: "flex",
    alignItems: "center",
    height: appThemeSize.tableFilterControlHeight,
    boxSizing: "border-box",
    fontSize: "11.5px",
    fontFamily: uiFont,
    lineHeight: 1.2,
  },
}));

export const FilterEmptyDash = styled("span")(() => ({
  display: "inline-flex",
  alignItems: "center",
  height: appThemeSize.tableFilterControlHeight,
  color: appThemeColors.text.faintLight,
  fontSize: "13px",
  fontWeight: 400,
  fontFamily: uiFont,
  lineHeight: 1,
  userSelect: "none",
}));

export const EmptyStateText = styled(Typography)(() => ({
  padding: "44px 16px",
  fontSize: "13.5px",
  fontFamily: uiFont,
  color: appThemeColors.text.muted,
  textAlign: "center",
}));

export const DataTableEmptyStateContainer = styled(Box)({
  padding: "46px 20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: 10,
  color: appThemeColors.text.muted,
  fontFamily: uiFont,
  whiteSpace: "normal",
  width: "100%",
});

export const DataTableEmptyStateIconWrap = styled(Box)({
  width: 56,
  height: 56,
  borderRadius: 16,
  backgroundColor: appThemeColors.background.fill,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: appThemeColors.text.faint,
  "& .MuiSvgIcon-root": {
    fontSize: 26,
  },
});

export const DataTableEmptyStateTitle = styled(Typography)({
  fontFamily: uiFont,
  fontSize: 15,
  fontWeight: 600,
  color: appThemeColors.text.heading,
  margin: 0,
  textAlign: "center",
});

export const DataTableEmptyStateHint = styled(Typography)({
  fontFamily: uiFont,
  fontSize: 13,
  margin: 0,
  maxWidth: 340,
  width: "100%",
  color: appThemeColors.text.muted,
  textAlign: "center",
  whiteSpace: "normal",
});

export const EmptyStateClearLink = styled("button")(() => ({
  border: 0,
  background: "none",
  padding: 0,
  margin: 0,
  color: primary,
  textDecoration: "underline",
  cursor: "pointer",
  fontSize: "13.5px",
  fontFamily: uiFont,
  "&:hover": {
    color: appThemeColors.text.heading,
  },
}));

export const EmptyStateLoader = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingTop: 24,
  paddingBottom: 24,
}));

export const TableScrollShell = styled(Box)(() => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  minWidth: 0,
}));

export const TableBodyLoader = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 4,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  pointerEvents: "none",
  fontSize: "13px",
  color: appThemeColors.text.muted,
  fontFamily: uiFont,
}));

export const TableFooterBar = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "14px",
  rowGap: "8px",
  flexShrink: 0,
  padding: "12px 16px",
  backgroundColor: appThemeColors.background.paper2,
  borderTop: `1px solid ${appThemeColors.background.soft}`,
  zIndex: 2,
  color: appThemeColors.text.muted,
  fontSize: "12px",
  fontFamily: uiFont,
}));

export const FooterSpacer = styled(Box)({
  flex: 1,
});

export const PaginationWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginLeft: "auto",
});

export const SelectionSummary = styled(Typography)(() => ({
  color: appThemeColors.text.muted,
  fontSize: "12px",
  fontFamily: numericFont,
}));

export const FooterSelectionStack = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(0.5),
  minWidth: 0,
  maxWidth: "100%",
  [theme.breakpoints.up("sm")]: {
    maxWidth: 520,
  },
}));

export const StyledTablePagination = styled(TablePagination)(() => ({
  border: 0,
  margin: 0,
  padding: 0,
  color: appThemeColors.text.muted,
  fontFamily: uiFont,
  fontSize: "12px",
  "& .MuiTablePagination-toolbar": {
    alignItems: "center",
    minHeight: 40,
    paddingLeft: 0,
    paddingRight: 0,
    fontFamily: uiFont,
    fontSize: "12px",
    gap: 12,
  },
  "& .MuiTablePagination-spacer": {
    display: "none",
  },
  "& .MuiTablePagination-selectLabel": {
    marginBottom: 0,
    marginTop: 0,
    alignSelf: "center",
    fontSize: "12px !important",
    fontFamily: `${uiFont} !important`,
    fontWeight: 400,
    lineHeight: 1.3,
    color: `${appThemeColors.text.muted} !important`,
  },
  "& .MuiTablePagination-displayedRows": {
    marginBottom: 0,
    marginTop: 0,
    alignSelf: "center",
    fontSize: "12px !important",
    fontFamily: `${numericFont} !important`,
    fontWeight: 400,
    lineHeight: 1.3,
    color: `${appThemeColors.text.muted} !important`,
    whiteSpace: "nowrap",
  },
  "& .MuiTablePagination-actions": {
    marginLeft: 0,
    alignSelf: "center",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  "& .MuiTablePagination-actions .MuiIconButton-root + .MuiIconButton-root": {
    marginLeft: 0,
  },
  "& .MuiTablePagination-input": {
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 7,
    marginRight: 0,
    alignSelf: "center",
    display: "inline-flex",
    alignItems: "center",
  },
  "& .MuiInputBase-root": {
    marginTop: 0,
    marginBottom: 0,
    alignItems: "center",
    height: 30,
    fontSize: "12px !important",
    fontFamily: `${uiFont} !important`,
    borderRadius: 7,
    backgroundColor: appThemeColors.background.paper,
    border: `1px solid ${appThemeColors.border.strong}`,
    color: appThemeColors.text.primary,
    "&:before, &:after": {
      display: "none",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  "& .MuiTablePagination-select, & .MuiSelect-select": {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "6px !important",
    paddingRight: "14px !important",
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1.3,
    minHeight: "auto",
    height: 30,
    fontSize: "12px !important",
    fontFamily: `${uiFont} !important`,
    fontWeight: 400,
    color: `${appThemeColors.text.primary} !important`,
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": {
    top: "calc(50% - 0.5em)",
    right: 4,
    color: appThemeColors.text.muted,
    fontSize: "12px !important",
  },
  "& .MuiIconButton-root": {
    color: appThemeColors.text.primary,
    borderRadius: appThemeRadius.md,
    border: `1px solid ${appThemeColors.border.strong}`,
    backgroundColor: appThemeColors.background.paper,
    width: 30,
    height: 30,
    padding: 0,
    margin: 0,
    "& .MuiSvgIcon-root": {
      fontSize: 15,
    },
    "&.Mui-disabled": {
      color: appThemeColors.text.faintLight,
      borderColor: appThemeColors.border.default,
      opacity: 0.55,
    },
    "&:hover": {
      backgroundColor: appThemeColors.background.kbd,
      color: appThemeColors.text.primary,
    },
  },
}));

export const VisuallyHidden = styled("span")(visuallyHidden);
